import { CustomHeader } from "@/components/CustomHeader";
import { PostCard } from "@/components/PostCard";
import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import { Button } from "@/components/ui/Button";
import { VerifiedDisplayName } from "@/components/ui/VerifiedDisplayName";
import { useAuth } from "@/context/AuthContext";
import {
  collection as firestoreCollection,
  doc,
  getFirestore,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { getApp } from "firebase/app";
import { Post } from "@/types/post";
import { UserDocument } from "@/types/user";
import { mapDocToPost } from "@/utils/mapDocToPost.web";
import { Image } from "expo-image";
import { router, useLocalSearchParams } from "expo-router";
import Head from "expo-router/head";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActivityIndicator,
  FlatList,
  StyleSheet,
  TouchableOpacity,
} from "react-native";
import { IconSymbol } from "../../components/ui/IconSymbol";
import { useFormatDate } from "../../utils/formatDate";

const firestore = getFirestore(getApp());

const DefaultProfileImage = require("../../assets/images/default-profile.webp");

const PublicProfileScreen = () => {
  const { userId } = useLocalSearchParams<{ userId: string }>();
  const { user } = useAuth();
  const [userData, setUserData] = useState<UserDocument | null>(null);
  const [postedPosts, setPostedPosts] = useState<Post[]>([]);
  const [receivedPosts, setReceivedPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("posted");
  const [receivedPostsFetched, setReceivedPostsFetched] = useState(false);
  const { t } = useTranslation();
  const { formatMonthYear } = useFormatDate();

  const isOwnProfile = user?.uid === userId;

  useEffect(() => {
    if (!userId) return;

    setLoading(true);
    const userDocRef = doc(firestore, "users", userId);
    const unsubscribeUser = onSnapshot(
      userDocRef,
      (userDocSnap) => {
        if (userDocSnap.exists()) {
          const data = userDocSnap.data() as UserDocument;
          setUserData(data);
        } else {
          setUserData(null);
        }
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching user data:", error);
        setLoading(false);
      }
    );

    let postedPostsQuery = query(
      firestoreCollection(firestore, "posts"),
      where("author.uid", "==", userId),
      where("groupId", "==", null),
      orderBy("createdAt", "desc")
    );

    if (!isOwnProfile) {
      postedPostsQuery = query(
        postedPostsQuery,
        where("plateFollowersOnly", "==", false)
      );
    }
    const unsubscribePostedPosts = onSnapshot(
      postedPostsQuery,
      (snapshot) => {
        const postsData = (snapshot?.docs as any[]).map(mapDocToPost);
        setPostedPosts(postsData);
      },
      (error) => {
        console.error("Error fetching posts in public-profile:", error);
      }
    );

    return () => {
      unsubscribeUser();
      unsubscribePostedPosts();
    };
  }, [userId, isOwnProfile]);

  useEffect(() => {
    if (
      activeTab === "received" &&
      isOwnProfile &&
      !receivedPostsFetched &&
      userData?.followedPlates &&
      userData.followedPlates.length > 0
    ) {
      const receivedPostsQuery = query(
        firestoreCollection(firestore, "posts"),
        where("vehiclePlates", "array-contains-any", userData.followedPlates),
        orderBy("createdAt", "desc")
      );

      const unsubscribeReceivedPosts = onSnapshot(
        receivedPostsQuery,
        (snapshot) => {
          const postsData = (snapshot?.docs as any[]).map(mapDocToPost);
          setReceivedPosts(postsData);
          setReceivedPostsFetched(true);
        },
        (error) => {
          console.error(
            "Error fetching received posts in public-profile:",
            error
          );
        }
      );
      return () => unsubscribeReceivedPosts();
    }
  }, [activeTab, isOwnProfile, userData, receivedPostsFetched]);

  if (loading) {
    return (
      <ThemedView style={styles.centered}>
        <Head>
          <title>{t("PAGE_TITLE_PUBLIC_PROFILE")}</title>
        </Head>
        <ActivityIndicator />
      </ThemedView>
    );
  }

  if (!userData) {
    return (
      <ThemedView style={styles.centered}>
        <Head>
          <title>{t("PAGE_TITLE_PUBLIC_PROFILE")}</title>
        </Head>
        <ThemedText>{t("USER_NOT_FOUND")}</ThemedText>
      </ThemedView>
    );
  }

  const renderEmptyComponent = () => (
    <ThemedView style={styles.centered}>
      <ThemedText style={styles.noPostsText}>
        {activeTab === "posted"
          ? t("NO_POSTS_YET")
          : t("NO_RECEIVED_POSTS_YET")}
      </ThemedText>
    </ThemedView>
  );

  return (
    <ThemedView style={{ flex: 1 }}>
      <Head>
        <title>{t("PAGE_TITLE_PUBLIC_PROFILE")}</title>
      </Head>
      <CustomHeader
        showBackButton={true}
        containerStyle={{ borderBottomWidth: 0, paddingRight: 20 }}
      >
        {isOwnProfile && (
          <ThemedView
            style={{
              alignItems: "flex-end",
            }}
          >
            <Button
              text={t("EDIT_PROFILE")}
              onPress={() => router.push("/(tabs)/profile")}
              style={{
                borderRadius: 20,
                backgroundColor: "transparent",
                borderWidth: 1,
                borderColor: "gray",
                width: "auto",
                paddingVertical: 6,
                paddingHorizontal: 12,
              }}
              textStyle={{ color: "black" }}
            />
          </ThemedView>
        )}
      </CustomHeader>
      <FlatList
        ListHeaderComponent={
          <>
            <ThemedView style={styles.header}>
              <ThemedView style={{ alignItems: "center" }}>
                <Image
                  key={userId}
                  source={{ uri: userData?.photoURL }}
                  style={styles.profilePic}
                  placeholder={DefaultProfileImage}
                  cachePolicy="disk"
                  transition={300}
                  accessibilityLabel={t("USER_AVATAR_ALT")}
                />
                <VerifiedDisplayName
                  user={userData}
                  style={styles.name}
                  fallbackToUsername={false}
                  badgeSize={22}
                  containerStyle={{ justifyContent: "center" }}
                />
                {userData?.username && (
                  <ThemedText style={styles.username}>
                    @{userData.username}
                  </ThemedText>
                )}
              </ThemedView>
              <ThemedView style={styles.userDetails}>
                <IconSymbol size={20} name="calendar" color={"gray"} />
                <ThemedText style={{ color: "gray", marginLeft: 5 }}>
                  {t("JOINED", { date: formatMonthYear(userData.createdAt) })}
                </ThemedText>
              </ThemedView>
            </ThemedView>
            <ThemedView style={styles.details}>
              {userData?.bio && (
                <ThemedText style={styles.bio}>{userData.bio}</ThemedText>
              )}
            </ThemedView>
            <ThemedView style={styles.tabsContainer}>
              <TouchableOpacity
                style={[
                  styles.tab,
                  activeTab === "posted" && styles.activeTab,
                ]}
                onPress={() => setActiveTab("posted")}
              >
                <ThemedText
                  style={[
                    styles.tabText,
                    activeTab === "posted" && styles.activeTabText,
                  ]}
                >
                  {t("POSTED")}
                </ThemedText>
              </TouchableOpacity>
              {isOwnProfile && (
                <TouchableOpacity
                  style={[
                    styles.tab,
                    activeTab === "received" && styles.activeTab,
                  ]}
                  onPress={() => setActiveTab("received")}
                >
                  <ThemedText
                    style={[
                      styles.tabText,
                      activeTab === "received" && styles.activeTabText,
                    ]}
                  >
                    {t("RECEIVED")}
                  </ThemedText>
                </TouchableOpacity>
              )}
            </ThemedView>
          </>
        }
        data={activeTab === "posted" ? postedPosts : receivedPosts}
        keyExtractor={(item) => item.id}
        renderItem={({ item }) => <PostCard post={item} />}
        ListEmptyComponent={renderEmptyComponent}
      />
    </ThemedView>
  );
};

const styles = StyleSheet.create({
  centered: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
  },
  header: {
    padding: 20,
    paddingTop: 0,
  },
  profilePic: {
    width: 150,
    height: 150,
    borderRadius: 75,
    marginBottom: 10,
  },
  name: {
    fontSize: 24,
    fontWeight: "bold",
  },
  username: {
    fontSize: 16,
    color: "gray",
  },
  userDetails: {
    flexDirection: "row",
    alignItems: "center",
    color: "gray",
    marginTop: 15,
  },
  details: {
    paddingHorizontal: 20,
    paddingBottom: 20,
  },
  bio: {
    fontSize: 16,
    textAlign: "left",
  },
  tabsContainer: {
    flexDirection: "row",
    justifyContent: "space-around",
    borderBottomWidth: 1,
    borderBottomColor: "lightgray",
  },
  tab: {
    paddingVertical: 10,
    flex: 1,
    alignItems: "center",
  },
  activeTab: {
    borderBottomWidth: 2,
    borderBottomColor: "black",
  },
  tabText: {
    color: "gray",
  },
  activeTabText: {
    color: "black",
    fontWeight: "bold",
  },
  noPostsText: {
    textAlign: "center",
    marginTop: 20,
    color: "gray",
  },
});

export default PublicProfileScreen;
